import { motion } from "framer-motion";
import { MessageCircle, Info, ArrowUpRight } from "lucide-react";
import { CATEGORIES, pad, SERVICES_TOTAL, ALL_SERVICES } from "@/lib/catalog";
import { inr, scrollToId } from "@/lib/api";
import { waFor } from "@/components/FloatingWhatsApp";
import { track } from "@/lib/analytics";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Disclosure } from "@/sections/Packages";

const onSpot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
};

const ServiceCard = ({ s, n, onPay, paying, onConsult }) => (
  <motion.article initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.7, delay: (n % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }} onMouseMove={onSpot}
    className="spot-card group relative flex h-full flex-col rounded-3xl border border-white/10 bg-[#0E1526] p-6" data-testid={`service-card-${s.id}`}>
    <div className="relative z-[2] flex items-start justify-between gap-3">
      <span className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 font-mono text-sm text-[#00F0FF] transition-colors group-hover:bg-[#00F0FF] group-hover:text-[#070B14]" data-testid={`service-card-${s.id}-number`}>{pad(n)}</span>
      <div className="text-right">
        <p className="font-mono text-xl font-bold text-white" data-testid={`service-card-${s.id}-price`}>{inr(s.price)}</p>
        {s.unit && <p className="text-[11px] text-slate-400">{s.unit}</p>}
      </div>
    </div>
    <h4 className="relative z-[2] mt-6 font-display text-lg font-bold leading-snug text-white">{s.name}</h4>
    {s.note && <p className="relative z-[2] mt-1 font-mono text-[11px] uppercase tracking-wider text-slate-400">{s.note}</p>}
    <p className="relative z-[2] mt-2 text-sm text-slate-300">{s.desc}</p>
    {s.fee && <p className="relative z-[2] mt-3 flex gap-2 text-xs text-amber-200/90" data-testid={`service-card-${s.id}-fee`}><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{s.fee}</p>}
    <div className="relative z-[2] mt-auto space-y-2 pt-6">
      <button onClick={() => onConsult(s.name, "service_card")} data-testid={`service-card-${s.id}-consult-button`}
        className="w-full rounded-full bg-[#00F0FF] px-3 py-2.5 text-sm font-semibold text-[#070B14] transition-transform hover:scale-[1.02]">
        Book Free Consultation
      </button>
      <div className="flex gap-2">
        <a href={waFor(`${s.name} (${inr(s.price)})`)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { location: "service_card", item: s.name })}
          data-testid={`service-card-${s.id}-whatsapp-button`}
          className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/20 px-3 py-2.5 text-sm text-white transition-colors hover:border-[#25D366]">
          <MessageCircle className="h-4 w-4" /> Enquire on WhatsApp
        </a>
        {!s.actual && (
          <button onClick={() => onPay([s.id], s.name)} disabled={paying} data-testid={`service-card-${s.id}-pay-button`} aria-label={`Pay for ${s.name}`}
            className="rounded-full border border-white/20 px-3 py-2.5 text-sm text-white transition-colors hover:border-[#00F0FF] disabled:opacity-50">
            Pay
          </button>
        )}
      </div>
    </div>
  </motion.article>
);

export const ServiceCatalog = ({ onPay, paying, onConsult }) => (
  <section id="pricing" className="relative border-t border-white/10 bg-[#0B111F] py-24 sm:py-32" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
      <Reveal>
        <Eyebrow index="03">Services &amp; pricing</Eyebrow>
        <h2 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-white sm:text-6xl">
          Individual services <span className="text-slate-400">&amp; prices.</span>
        </h2>
        <p className="mt-4 max-w-2xl text-sm text-slate-300 sm:text-base">Choose only what you need. All prices are in Indian Rupees (₹).</p>
      </Reveal>
      <nav className="mt-10 flex gap-2 overflow-x-auto pb-2" data-testid="service-category-nav" data-lenis-prevent>
        {CATEGORIES.map((c, i) => (
          <button key={c.key} onClick={() => scrollToId(`cat-${c.key}`)} data-testid={`service-category-nav-${c.key}`}
            className="shrink-0 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 transition-colors hover:border-[#00F0FF] hover:text-white">
            <span className="mr-2 font-mono text-xs text-[#00F0FF]">{i + 1}</span>{c.name}
          </button>
        ))}
      </nav>
      <div className="mt-12 space-y-16">
        {CATEGORIES.map((c, ci) => (
          <div key={c.key} id={`cat-${c.key}`} data-testid={`service-category-${c.key}`}>
            <Reveal>
              <div className="flex flex-col gap-4 border-b border-white/10 pb-5 sm:flex-row sm:items-end sm:justify-between">
                <h3 className="font-display text-2xl font-bold text-white sm:text-3xl">
                  <span className="mr-3 font-mono text-sm text-[#00F0FF]">Category {ci + 1}</span><br className="sm:hidden" />{c.name}
                  {c.sub && <span className="mt-1 block text-base font-semibold text-slate-300">{c.sub} · Total category price {inr(c.items.reduce((a, s) => a + s.price, 0))}</span>}
                </h3>
                <button onClick={() => onConsult(c.items[0].name, `category_${c.key}`)} data-testid={`category-${c.key}-consult-button`}
                  className="w-fit shrink-0 rounded-full border border-[#00F0FF]/50 px-4 py-2 text-sm text-[#00F0FF] transition-colors hover:bg-[#00F0FF] hover:text-[#070B14]">
                  Book Free Consultation
                </button>
              </div>
            </Reveal>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
              {c.items.map((s) => <ServiceCard key={s.id} s={s} n={s.n} onPay={onPay} paying={paying} onConsult={onConsult} />)}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-12 flex flex-col justify-between gap-2 rounded-2xl border border-white/10 bg-[#0E1526] px-6 py-5 sm:flex-row sm:items-center" data-testid="services-total">
        <span className="text-sm text-slate-300">Total cost of all {ALL_SERVICES.length} individual services <span className="text-slate-400">(sum of the prices above, not a package price)</span></span>
        <span className="font-mono text-2xl font-bold text-white" data-testid="services-total-amount">{inr(SERVICES_TOTAL)}</span>
      </div>
      <Reveal className="mt-10">
        <div className="flex flex-col items-start justify-between gap-5 rounded-3xl border border-white/10 bg-[#0E1526] p-6 sm:flex-row sm:items-center sm:p-8" data-testid="services-quote-banner">
          <div>
            <p className="font-display text-xl font-bold text-white sm:text-2xl">Have a customised requirement?</p>
            <p className="mt-1 text-sm text-slate-300">Combine services or describe your project — we'll prepare a quote.</p>
          </div>
          <button onClick={() => onConsult("Custom requirement / Not sure yet", "request_quote")} data-testid="services-request-quote-button"
            className="group flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#070B14]">
            Request a Quote <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </button>
        </div>
      </Reveal>
      <Disclosure testId="services-disclosure" />
    </div>
  </section>
);
